import { getCurrentBranch } from '@/lib/config/branches-config';
import { localizePath } from '@/lib/config/utils';
import { organizationRef } from '@/lib/seo/organization-schema';

export interface CalculatorOption {
  value: string;
  label: string;
}

export interface TrussCalculationPageContent {
  breadcrumbTitle: string;
  heroBadge: string;
  heroTitle: string;
  heroDescription: string;
  calculatorTitle: string;
  calculatorDescription: string;
  calculatorNotice: string;
}

const PAGE_TEMPLATES = {
  breadcrumbTitle: 'Расчёт ферм',
  heroBadge: 'Подготовим расчёт ферм под ваш объект',
  heroTitle: 'Расчёт ферм',
  heroDescription:
    'Ответьте на несколько вопросов и получите индивидуальный расчёт стоимости ферм из металла',
  calculatorTitle: 'Оставьте заявку на расчёт ферм',
  calculatorDescription:
    'Укажите тип ферм, материал, пролёт, покрытие и количество, оставьте телефон и при необходимости комментарий. После отправки специалисты свяжутся с вами и подготовят индивидуальный расчёт.',
  calculatorNotice:
    'Итоговая стоимость на странице не отображается. После отправки заявки специалисты подготовят индивидуальный расчёт по вашему запросу.',
};

const META_TEMPLATES = {
  title: 'Расчёт ферм | Форма заявки',
  description:
    'Оставьте заявку на расчёт ферм. Ответьте на несколько вопросов по объекту и получите индивидуальный расчёт от ЗМК Континенталь.',
  keywords:
    'расчёт ферм, расчет ферм, ферма металлическая, форма заявки на расчет ферм, ЗМК Континенталь',
};

export const TRUSS_CALCULATOR_OPTIONS = {
  trussTypes: [
    { value: 'triangular-single-slope', label: 'Треугольная односкатная' },
    { value: 'triangular-double-slope', label: 'Треугольная двускатная' },
    { value: 'arched', label: 'Арочная' },
    { value: 'trapezoidal', label: 'Трапециедальная' },
  ],
  materials: [
    { value: 'profile-pipe', label: 'Профильная труба' },
    { value: 'angle', label: 'Уголок' },
    { value: 'beam', label: 'Балка' },
  ],
  spans: [
    { value: '6m', label: '6 м' },
    { value: '12m', label: '12 м' },
    { value: '24m', label: '24 м' },
    { value: 'custom', label: 'Ваш вариант' },
  ],
  coatings: [
    { value: 'none', label: 'Без покрытия' },
    { value: 'primer', label: 'Грунтовка' },
    { value: 'primer-enamel', label: 'Грунт-эмаль' },
    { value: 'hot-galvanizing', label: 'Горячее цинкование' },
  ],
  quantities: [
    { value: '1', label: '1 шт' },
    { value: '5', label: '5 шт' },
    { value: '10', label: '10 шт' },
    { value: 'custom', label: 'Ваш вариант' },
  ],
} satisfies Record<string, CalculatorOption[]>;

export function getTrussCalculationPageContent(): TrussCalculationPageContent {
  return {
    breadcrumbTitle: PAGE_TEMPLATES.breadcrumbTitle,
    heroBadge: PAGE_TEMPLATES.heroBadge,
    heroTitle: PAGE_TEMPLATES.heroTitle,
    heroDescription: PAGE_TEMPLATES.heroDescription,
    calculatorTitle: PAGE_TEMPLATES.calculatorTitle,
    calculatorDescription: PAGE_TEMPLATES.calculatorDescription,
    calculatorNotice: PAGE_TEMPLATES.calculatorNotice,
  };
}

export function getTrussCalculationMetadata(langCode: string) {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://ur-met.ru';
  const canonicalPath = localizePath('/raschet-ferm/', langCode);
  const canonicalUrl = new URL(canonicalPath, siteUrl).toString();
  const title = META_TEMPLATES.title;
  const description = META_TEMPLATES.description;
  const keywords = META_TEMPLATES.keywords;

  return {
    title,
    description,
    keywords,
    alternates: {
      canonical: canonicalUrl,
    },
    openGraph: {
      title,
      description,
      url: canonicalUrl,
      siteName: 'ЗМК Континенталь',
      locale: 'ru_RU',
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        'max-video-preview': -1,
        'max-image-preview': 'large',
        'max-snippet': -1,
      },
    },
  };
}

export function getTrussCalculationStructuredData(langCode: string) {
  const branch = getCurrentBranch();
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://ur-met.ru';
  const canonicalPath = localizePath('/raschet-ferm/', langCode);
  const pageUrl = new URL(canonicalPath, siteUrl).toString();
  const content = getTrussCalculationPageContent();
  const meta = getTrussCalculationMetadata(langCode);
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Главная',
        item: new URL(localizePath('/', langCode), siteUrl).toString(),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: content.breadcrumbTitle,
        item: pageUrl,
      },
    ],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: content.heroTitle,
    description: meta.description,
    serviceType: 'Расчёт ферм',
    // Лендинг живёт только в петербургской ветке: next.config.js 301-редиректит
    // /geo/:geo/raschet-*/ на неgeo-версию. Отсюда и география филиала.
    areaServed: (branch?.seo?.areaServed ?? []).map((area) => ({
      '@type': area['@type'],
      name: area.name,
    })),
    provider: organizationRef(),
    url: pageUrl,
  };

  return {
    breadcrumbSchema,
    serviceSchema,
  };
}
